import { Button, Grid, GridColumn, Icon, Table } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import AnalyticsService from '../../../services/AnalyticsService';
import InvoiceDetailsStore from '../../../stores/InvoiceDetailsStore';
import { InvoicePaymentStore } from '../../../stores/InvoicePaymentStore';
import { ITranslateStore } from '../../../stores/TranslateStore';
import { formatAsCurrency } from '../../../util/FormatUtil';
import AccentedWithAlert from '../AccentedWithAlert';
import TotalPrepaymentAmount from '../TotalPrepaymentAmount';
import UnsettledReceivablesStore from '../../../stores/UnsettledReceivablesStore';
import { IInvoicesStore } from '../../../stores/InvoicesStore';
import { formatDate } from '../../../util/DateUtil';
import InvoiceNameStore from '../../../stores/InvoiceNameStore';

interface IUnpaidInvoicesTableProps {
  invoicesStore?: IInvoicesStore;
  unsettledReceivablesStore?: UnsettledReceivablesStore;
  translateStore?: ITranslateStore;
  invoiceDetailsStore?: InvoiceDetailsStore;
  invoicePaymentStore?: InvoicePaymentStore;
  invoiceNameStore?: InvoiceNameStore;
  analyticsService?: AnalyticsService;
}

@inject('invoicesStore', 'unsettledReceivablesStore', 'translateStore', 'invoiceDetailsStore', 'invoicePaymentStore', 'invoiceNameStore', 'analyticsService')
@observer
export default class UnpaidInvoicesTable extends React.Component<IUnpaidInvoicesTableProps> {
  public render(): JSX.Element {
    const translate = this.props.translateStore.translate;
    const unsettledReceivablesStore = this.props.unsettledReceivablesStore;
    const invoicesStore = this.props.invoicesStore;
    const analyticsService = this.props.analyticsService;

    return (
      <Table responsive="cols" break="md" className="text__margin-double">
        <thead>
          <tr>
            <th />
            <th>{translate('invoices.unpaid-invoices.invoices.date')}</th>
            <th>{translate('invoices.unpaid-invoices.invoices.name')}</th>
            <th>{translate('invoices.unpaid-invoices.invoices.deadline')}</th>
            <th className="text-right">{translate('invoices.unpaid-invoices.invoices.to-pay')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {unsettledReceivablesStore.unsettledReceivables.isFinished ? (
            unsettledReceivablesStore.unsettledReceivables.payload.unpaidInvoices.map((invoice, index) => (
              <tr key={index}>
                <td className="hidden-xs hidden-sm">
                  <Icon modifiers={this.props.invoiceNameStore.invoiceIconName(invoice.contentType)} size="sm" />
                </td>
                <td>{formatDate(invoice.issuedDate)}</td>
                <td className="text-bold">{invoicesStore.invoiceName(invoice)}</td>
                <td>
                  <AccentedWithAlert showAlert={invoicesStore.overdue(invoice)}>{formatDate(invoice.dueDate)}</AccentedWithAlert>
                </td>
                <td className={`c-brand-primary-text text-bold h5 text-right ${invoicesStore.overdue(invoice) ? 'text-sale' : ''}`}>
                  {formatAsCurrency(invoice.unsettledAmount)}
                </td>
                <td data-headless={true}>
                  <Grid verticalGutter="none" align={['middle-xs', 'end-md']}>
                    <GridColumn width={['xs']}>
                      <Button
                        text={translate('invoices.unpaid-invoices.invoices.view-invoice')}
                        variant="secondary"
                        size="small"
                        onClick={() => {
                          this.props.invoiceDetailsStore.activateInvoiceDetails(invoice, 'unpaid-invoices');
                          analyticsService.unpaidInvoicesViewDetails(invoice.contentType, invoice.invoiceNumber);
                        }}
                      />
                    </GridColumn>
                    <GridColumn width={['xs']}>
                      <Button
                        text={translate('invoices.unpaid-invoices.invoices.pay')}
                        size="small"
                        onClick={() => {
                          this.props.invoicePaymentStore.makePayment(invoice.invoiceNumber);
                          analyticsService.unpaidInvoicesPay(invoice.contentType, invoice.invoiceNumber);
                        }}
                      />
                    </GridColumn>
                  </Grid>
                </td>
              </tr>
            ))
          ) : (
            <React.Fragment />
          )}
          {unsettledReceivablesStore.totalPrepayment !== 0 ? this.prepaymentInformation() : <React.Fragment />}
        </tbody>
      </Table>
    );
  }

  // TODO: cannot move to separate component atm. See: WEB-16594
  private prepaymentInformation() {
    const translate = this.props.translateStore.translate;
    return (
      <tr>
        <td />
        <td />
        <td />
        <td data-headless={true} className="text-bold text-right">
          {translate('invoices.unpaid-invoices.invoices.amount-prepayment')}
        </td>
        <td data-headless={true} className="text-bold text-right h5">
          <TotalPrepaymentAmount />
        </td>
        <td />
      </tr>
    );
  }
}
